import { GetServerSidePropsContext } from "next";
import { isAPIError } from "lib/api/base";
import { Invoice, PurchaseSubscriptionResponse } from "lib/api/checkout";
import { getSubscription } from "lib/api/subscription";
import { getCompletedGuidedSignUpQuiz } from "utils/contentful/helpers";
import { PlanPrepaidPricing } from "components/PlanSelection/planSelectionUtil";

export const getEstimatedLTV = async ({
  contentfulQuizModelRaw,
  ctx,
}: {
  contentfulQuizModelRaw: any;
  ctx?: GetServerSidePropsContext;
}) => {
  const completedGuidedSignUpQuiz = await getCompletedGuidedSignUpQuiz({
    cmsGuidedSignUpReferences: contentfulQuizModelRaw,
    ctx,
  });
  return completedGuidedSignUpQuiz?.ltvEstimate || null;
};

export const getSubscriptionPurchaseInvoice = (
  response: PurchaseSubscriptionResponse
) => {
  return Array.isArray(response) ? response[0].invoice : response.invoice;
};

export const getSubscriptionFromInvoice = async (
  invoice: Invoice,
  ctx?: GetServerSidePropsContext
) => {
  if (!invoice) {
    return null;
  }

  const invoiceSub = invoice?.items?.find((i) => i.type === "subscription");
  const subRes = await getSubscription({ id: invoiceSub?.object?.id }, ctx);
  const subscription = isAPIError(subRes) ? null : subRes.subscription;
  return subscription;
};

export const getNumericValueFromCurrencyString = (currencyString?: string) => {
  return Number(currencyString?.replace(/[^0-9.-]+/g, ""));
};

export const formatNumberAsUSCurrency = (number: number) => {
  const formatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  });

  return formatter.format(number);
};

export const getPlanPrepaidExperimentLineItems = ({
  lineItems,
  selectedPlanPrepaidPricing,
}: {
  lineItems: (string | boolean | undefined)[][];
  selectedPlanPrepaidPricing?: PlanPrepaidPricing | null;
}) => {
  const prepaidLineItems = [...lineItems];
  const {
    boxCount,
    boxSavingsCurrencyString,
    priceWithoutSavingsCurrencyString,
  } = selectedPlanPrepaidPricing || {};

  // replace subtotal with cumulative total
  const subtotalItem = prepaidLineItems.find((item) => item[3] === "subtotal");
  if (subtotalItem && priceWithoutSavingsCurrencyString) {
    subtotalItem[1] = priceWithoutSavingsCurrencyString;
  }

  // move coupon
  const couponIndex = prepaidLineItems.findIndex(
    (item) => item[3] === "coupon"
  );
  const couponItem = prepaidLineItems[couponIndex];
  prepaidLineItems.splice(couponIndex, 1);
  prepaidLineItems.splice(1, 0, couponItem);

  // insert box savings
  if (boxCount && boxSavingsCurrencyString) {
    const boxSavingsItem = [
      `${boxCount} Box Savings`,
      `-${boxSavingsCurrencyString}`,
      true,
      "boxSavings",
    ];
    prepaidLineItems.splice(2, 0, boxSavingsItem);
  }

  return prepaidLineItems;
};
